import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Plus,
  Ban,
  X,
  Move,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

/**
 * CoachCalendar — coach/admin scheduling calendar.
 *
 * v2 fixes (user-reported):
 *  - Events are bucketed by LOCAL calendar date (the old ms-division bucketing
 *    put sessions under the wrong day column around DST changes).
 *  - Sessions outside the 6am–8pm window clamp INTO the grid (with their real
 *    time always printed) instead of rendering off-grid.
 *  - Week/Day view toggle; phones default to Day view with taller rows.
 *  - Tap-to-move: tap a session → "Move" → tap any empty slot. Works on touch
 *    where HTML5 drag-and-drop does not. Desktop drag still works too.
 *  - Tapping a session opens an action sheet (details / move / cancel).
 */

const HOURS = Array.from({ length: 15 }, (_, i) => 6 + i); // 6am..8pm rows
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

function startOfWeek(d) {
  const out = new Date(d);
  out.setDate(out.getDate() - out.getDay());
  out.setHours(0, 0, 0, 0);
  return out;
}

function startOfDay(d) {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function addDays(d, n) {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function fmtDay(d) {
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function fmtTime(d) {
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

const fmtHour = (h) => (h > 12 ? `${h - 12}pm` : h === 12 ? "12pm" : `${h}am`);

/** Minutes since 6am (local). */
function offsetMin(date) {
  return date.getHours() * 60 + date.getMinutes() - HOURS[0] * 60;
}

const GRID_MIN = HOURS.length * 60;

export default function CoachCalendar() {
  const [view, setView] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 768 ? "day" : "week"
  );
  const [anchor, setAnchor] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 768
      ? startOfDay(new Date())
      : startOfWeek(new Date())
  );
  const [sessions, setSessions] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [clients, setClients] = useState([]);
  const [modal, setModal] = useState(null); // create session/block modal
  const [action, setAction] = useState(null); // session action sheet
  const [moving, setMoving] = useState(null); // { id, label, duration_min } — tap-to-move mode
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(null); // desktop HTML5 drag

  const hourPx = view === "day" ? 56 : 44;

  const load = async () => {
    const [s, b, c] = await Promise.all([
      api.get("/sessions/admin/upcoming").catch(() => ({ data: [] })),
      api.get("/sessions/blocks").catch(() => ({ data: [] })),
      api.get("/coaching/clients").catch(() => ({ data: [] })),
    ]);
    setSessions(s.data || []);
    setBlocks(b.data || []);
    setClients(c.data || []);
  };
  useEffect(() => {
    load();
  }, []);

  const dayCount = view === "day" ? 1 : 7;
  const days = useMemo(
    () => Array.from({ length: dayCount }, (_, i) => addDays(anchor, i)),
    [anchor, dayCount]
  );

  // Bucket events by LOCAL calendar date — immune to DST length-of-day drift.
  const dayEvents = useMemo(() => {
    const buckets = days.map(() => ({ sessions: [], blocks: [] }));
    for (const s of sessions) {
      const dt = new Date(s.scheduled_at);
      const di = days.findIndex((d) => sameDay(d, dt));
      if (di >= 0) buckets[di].sessions.push(s);
    }
    for (const b of blocks) {
      const start = new Date(b.start_at);
      const end = new Date(b.end_at);
      days.forEach((day, di) => {
        const dayStart = startOfDay(day);
        const dayEnd = addDays(dayStart, 1);
        if (end <= dayStart || start >= dayEnd) return;
        buckets[di].blocks.push({
          ...b,
          _slice_start: start > dayStart ? start : dayStart,
          _slice_end: end < dayEnd ? end : dayEnd,
        });
      });
    }
    return buckets;
  }, [sessions, blocks, days]);

  const navigate = (dir) => {
    setAnchor(addDays(anchor, dir * (view === "day" ? 1 : 7)));
  };

  const goToday = () => setAnchor(view === "day" ? startOfDay(new Date()) : startOfWeek(new Date()));

  const switchView = (v) => {
    setView(v);
    setAnchor(v === "day" ? startOfDay(new Date()) : startOfWeek(new Date()));
  };

  const reschedule = async (session, day, hour) => {
    const newAt = new Date(day);
    newAt.setHours(hour, 0, 0, 0);
    if (newAt < new Date()) return toast.error("That slot is in the past");
    try {
      await api.patch(`/sessions/${session.id}`, {
        scheduled_at: newAt.toISOString(),
        duration_min: session.duration_min || 60,
      });
      toast.success("Session moved — client notified");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reschedule");
    }
  };

  const cancelSession = async (session) => {
    if (!window.confirm(`Cancel ${session.client_name || "this client"}'s session? They'll be notified.`)) return;
    try {
      await api.post(`/sessions/${session.id}/staff-cancel`);
      toast.success("Session cancelled — client notified");
      setAction(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to cancel");
    }
  };

  const handleCellTap = (day, hour) => {
    if (moving) {
      // Tap-to-move: this tap places the session being moved.
      const s = moving;
      setMoving(null);
      reschedule(s, day, hour);
      return;
    }
    const scheduled_at = new Date(day);
    scheduled_at.setHours(hour, 0, 0, 0);
    if (scheduled_at < new Date()) return toast.error("That slot is in the past");
    setModal({ mode: "session", scheduled_at, day, hour, duration_min: 60, client_id: "", reason: "", block_end_hour: hour + 1 });
  };

  const createSession = async () => {
    if (!modal.client_id) return toast.error("Pick a client");
    setSaving(true);
    try {
      await api.post("/sessions/staff-book", {
        client_id: modal.client_id,
        scheduled_at: modal.scheduled_at.toISOString(),
        duration_min: Number(modal.duration_min),
        session_type: modal.session_type || "in_person",
        note: modal.note || null,
      });
      toast.success("Session booked — client notified");
      setModal(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to book");
    } finally {
      setSaving(false);
    }
  };

  const createBlock = async () => {
    const start = new Date(modal.day);
    start.setHours(modal.hour, 0, 0, 0);
    const end = new Date(modal.day);
    end.setHours(Number(modal.block_end_hour), 0, 0, 0);
    if (end <= start) return toast.error("End must be after start");
    setSaving(true);
    try {
      await api.post("/sessions/blocks", {
        start_at: start.toISOString(),
        end_at: end.toISOString(),
        reason: modal.reason || null,
      });
      toast.success("Time blocked");
      setModal(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to block");
    } finally {
      setSaving(false);
    }
  };

  const deleteBlock = async (blockId) => {
    if (!window.confirm("Remove this block?")) return;
    try {
      await api.delete(`/sessions/blocks/${blockId}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const headerLabel =
    view === "day"
      ? days[0].toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })
      : `${days[0].toLocaleDateString(undefined, { month: "long", day: "numeric" })} — ${days[
          days.length - 1
        ].toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;

  return (
    <div className="obs-card" data-testid="coach-calendar">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <CalendarDays className="h-5 w-5 text-blaze shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="overline">Calendar</div>
          <h3 className="font-heading text-xl sm:text-2xl uppercase truncate">{headerLabel}</h3>
        </div>
        <div className="flex gap-1 items-center">
          <div className="flex border border-obsidian-600 rounded-sm overflow-hidden mr-1">
            {["day", "week"].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => switchView(v)}
                data-testid={`cal-view-${v}`}
                className={`px-3 py-2 text-[10px] uppercase tracking-widest ${
                  view === v ? "bg-blaze text-obsidian-900 font-bold" : "text-zinc-400 hover:text-blaze"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
          <button type="button" onClick={() => navigate(-1)} className="ghost-btn p-2" data-testid="cal-prev" aria-label="Previous">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button type="button" onClick={goToday} className="ghost-btn px-3 py-2 text-[11px] uppercase tracking-widest" data-testid="cal-today">
            Today
          </button>
          <button type="button" onClick={() => navigate(1)} className="ghost-btn p-2" data-testid="cal-next" aria-label="Next">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Move-mode banner — tap-to-move works on phones where drag can't. */}
      {moving ? (
        <div
          className="mb-3 flex items-center gap-3 border border-blaze bg-blaze/10 rounded-sm px-3 py-2"
          data-testid="cal-move-banner"
        >
          <Move className="h-4 w-4 text-blaze shrink-0" />
          <div className="flex-1 text-xs text-zinc-200">
            Moving <span className="text-blaze font-bold">{moving.label}</span> — tap any open slot
          </div>
          <button
            type="button"
            onClick={() => setMoving(null)}
            data-testid="cal-move-cancel"
            className="ghost-btn px-3 py-1.5 text-[10px] uppercase tracking-widest"
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase tracking-widest text-zinc-500" data-testid="cal-legend">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm border border-blaze/70 bg-blaze/25 inline-block" /> Tap empty slot to book
          </div>
          <div className="flex items-center gap-1.5">
            <span
              className="w-3 h-3 rounded-sm inline-block"
              style={{ background: "repeating-linear-gradient(45deg, rgba(239,68,68,0.55) 0 3px, rgba(239,68,68,0.85) 3px 6px)" }}
            />{" "}
            Blocked
          </div>
          <div>Tap a session to move or cancel it</div>
        </div>
      )}

      <div className={view === "week" ? "overflow-x-auto" : ""}>
        <div
          className={view === "week" ? "min-w-[720px] grid" : "grid"}
          style={{ gridTemplateColumns: `48px repeat(${dayCount}, 1fr)` }}
        >
          {/* Header row */}
          <div />
          {days.map((d) => (
            <div key={d.toISOString()} className="text-center py-2 border-b border-obsidian-600">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                {d.toLocaleDateString(undefined, { weekday: "short" })}
              </div>
              <div className={`font-heading text-lg ${sameDay(d, new Date()) ? "text-blaze" : "text-zinc-200"}`}>
                {d.getDate()}
              </div>
            </div>
          ))}

          {/* Hour rows */}
          {HOURS.map((h) => (
            <React.Fragment key={h}>
              <div
                className="text-[10px] text-zinc-500 uppercase tracking-widest text-right pr-2 border-t border-obsidian-600/60"
                style={{ height: hourPx }}
              >
                {h > 12 ? `${h - 12}p` : h === 12 ? "12p" : `${h}a`}
              </div>
              {days.map((d, di) => (
                <button
                  key={di + "-" + h}
                  type="button"
                  onClick={() => !dragging && handleCellTap(d, h)}
                  onDragOver={(e) => {
                    if (dragging) e.preventDefault();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragging) {
                      reschedule(dragging, d, h);
                      setDragging(null);
                    }
                  }}
                  className={`border-t border-l border-obsidian-600/60 transition-colors text-left ${
                    moving ? "hover:bg-blaze/20 bg-blaze/[0.03]" : "hover:bg-blaze/5"
                  }`}
                  style={{ height: hourPx }}
                  data-testid={`cal-cell-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}-${h}`}
                  aria-label={`Schedule ${d.toDateString()} ${h}:00`}
                />
              ))}
            </React.Fragment>
          ))}

          {/* Event overlays per day column. pointer-events pass through empty
              space; events themselves are interactive unless drag/move is in
              progress (so the tap lands on the slot underneath). */}
          {days.map((d, di) => (
            <div
              key={"ev" + di}
              className="relative pointer-events-none"
              style={{ gridColumnStart: di + 2, gridRowStart: 2, gridRowEnd: 2 + HOURS.length }}
            >
              {dayEvents[di].blocks.map((b) => {
                const start = b._slice_start;
                const end = b._slice_end;
                const topMin = Math.min(Math.max(0, offsetMin(start)), GRID_MIN - 20);
                const endMin = Math.min(Math.max(offsetMin(end), topMin + 20), GRID_MIN);
                return (
                  <div
                    key={b.id + di}
                    className={`absolute inset-x-0.5 ${moving || dragging ? "pointer-events-none" : "pointer-events-auto"} rounded-sm p-1 text-[10px] leading-tight overflow-hidden text-white`}
                    style={{
                      top: topMin * (hourPx / 60),
                      height: (endMin - topMin) * (hourPx / 60),
                      background:
                        "repeating-linear-gradient(45deg, rgba(239,68,68,0.35) 0 6px, rgba(239,68,68,0.55) 6px 12px)",
                      border: "1px solid rgba(239,68,68,0.7)",
                    }}
                    data-testid={`cal-block-${b.id}`}
                  >
                    <div className="flex items-start gap-1">
                      <Ban className="h-3 w-3 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold uppercase tracking-widest text-[9px]">Blocked</div>
                        {b.reason && <div className="truncate opacity-90">{b.reason}</div>}
                      </div>
                      <button
                        type="button"
                        onClick={() => deleteBlock(b.id)}
                        className="text-white/70 hover:text-white shrink-0"
                        aria-label="Remove block"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
              {dayEvents[di].sessions.map((s) => {
                const start = new Date(s.scheduled_at);
                const durMin = s.duration_min || 60;
                // Clamp INTO the visible grid; the printed time is always real.
                const topMin = Math.min(Math.max(0, offsetMin(start)), GRID_MIN - 24);
                const height = Math.max(24, Math.min(durMin, GRID_MIN - topMin) * 1) * (hourPx / 60);
                return (
                  <div
                    key={s.id}
                    draggable={!moving}
                    onDragStart={(e) => {
                      setDragging(s);
                      e.dataTransfer.effectAllowed = "move";
                      try { e.dataTransfer.setData("text/plain", s.id); } catch {}
                    }}
                    onDragEnd={() => setDragging(null)}
                    className={`absolute inset-x-0.5 ${moving || dragging ? "pointer-events-none" : "pointer-events-auto"} rounded-sm border border-blaze/70 bg-blaze/25 hover:bg-blaze/40 transition-colors p-1 text-[10px] leading-tight overflow-hidden cursor-pointer ${
                      dragging?.id === s.id || moving?.id === s.id ? "opacity-40" : ""
                    }`}
                    style={{ top: topMin * (hourPx / 60), height }}
                    data-testid={`cal-session-${s.id}`}
                    onClick={() => setAction(s)}
                  >
                    <div className="font-bold uppercase tracking-widest text-blaze text-[9px] flex items-center gap-1">
                      {fmtTime(start)}
                      {s.session_type === "online" && (
                        <span className="text-sky-400 normal-case">· online</span>
                      )}
                    </div>
                    <div className="text-zinc-100 truncate">{s.client_name || "Client"}</div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Session action sheet: details / move / cancel */}
      {action && (
        <div
          className="fixed inset-0 bg-black/70 z-40 grid place-items-center p-4"
          onClick={() => setAction(null)}
          data-testid="cal-session-actions"
        >
          <div className="bg-obsidian-800 border border-obsidian-600 rounded-sm w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-heading text-xl uppercase">{action.client_name || "Client"}</h4>
              <button type="button" onClick={() => setAction(null)} className="text-zinc-500 hover:text-white" data-testid="cal-action-close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-zinc-400 mb-1">
              {fmtDay(new Date(action.scheduled_at))} · {fmtTime(new Date(action.scheduled_at))} · {action.duration_min || 60} min ·{" "}
              <span className={action.session_type === "online" ? "text-sky-400" : "text-blaze"}>
                {action.session_type === "online" ? "Online video" : "In person"}
              </span>
            </p>
            {action.note && <p className="text-xs text-zinc-500 italic mb-2">“{action.note}”</p>}
            {action.client_email && <p className="text-[11px] text-zinc-500 mb-3">{action.client_email}</p>}
            <div className="space-y-2 mt-3">
              <button
                type="button"
                data-testid="cal-action-move"
                onClick={() => {
                  setMoving({ ...action, label: `${action.client_name || "client"} · ${fmtTime(new Date(action.scheduled_at))}` });
                  setAction(null);
                }}
                className="w-full blaze-btn px-4 py-2.5 text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2"
              >
                <Move className="h-3.5 w-3.5" /> Move to another slot
              </button>
              <button
                type="button"
                data-testid="cal-action-cancel-session"
                onClick={() => cancelSession(action)}
                className="w-full ghost-btn px-4 py-2.5 text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2 !border-red-500/50 !text-red-400 hover:!bg-red-500/10"
              >
                <Trash2 className="h-3.5 w-3.5" /> Cancel session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: create session or block */}
      {modal && (
        <div className="fixed inset-0 bg-black/70 z-40 grid place-items-center p-4" onClick={() => setModal(null)} data-testid="cal-modal">
          <div className="bg-obsidian-800 border border-obsidian-600 rounded-sm w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-heading text-xl uppercase">
                {fmtDay(modal.day)} · {fmtHour(modal.hour)}
              </h4>
              <button type="button" onClick={() => setModal(null)} className="text-zinc-500 hover:text-white" data-testid="cal-modal-close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setModal({ ...modal, mode: "session" })}
                className={`flex-1 px-3 py-2 border rounded-sm text-[11px] uppercase tracking-widest transition-colors ${
                  modal.mode === "session" ? "bg-blaze text-obsidian-900 border-blaze" : "border-obsidian-600 text-zinc-400 hover:border-blaze hover:text-blaze"
                }`}
                data-testid="cal-mode-session"
              >
                <Plus className="h-3 w-3 inline mr-1" /> New session
              </button>
              <button
                type="button"
                onClick={() => setModal({ ...modal, mode: "block" })}
                className={`flex-1 px-3 py-2 border rounded-sm text-[11px] uppercase tracking-widest transition-colors ${
                  modal.mode === "block" ? "bg-blaze text-obsidian-900 border-blaze" : "border-obsidian-600 text-zinc-400 hover:border-blaze hover:text-blaze"
                }`}
                data-testid="cal-mode-block"
              >
                <Ban className="h-3 w-3 inline mr-1" /> Block time
              </button>
            </div>

            {modal.mode === "session" ? (
              <div className="space-y-3">
                <label className="block">
                  <div className="overline mb-1">Client</div>
                  <select
                    value={modal.client_id}
                    onChange={(e) => setModal({ ...modal, client_id: e.target.value })}
                    className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none text-sm"
                    data-testid="cal-modal-client"
                  >
                    <option value="">Pick a client…</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.first_name || c.email}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <div className="overline mb-1">Session type</div>
                  <select
                    value={modal.session_type || "in_person"}
                    onChange={(e) => setModal({ ...modal, session_type: e.target.value })}
                    className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none text-sm"
                    data-testid="cal-modal-type"
                  >
                    <option value="in_person">In person</option>
                    <option value="online">Online video</option>
                  </select>
                </label>
                <label className="block">
                  <div className="overline mb-1">Duration (min)</div>
                  <select
                    value={modal.duration_min}
                    onChange={(e) => setModal({ ...modal, duration_min: e.target.value })}
                    className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none text-sm"
                    data-testid="cal-modal-duration"
                  >
                    {[30, 45, 60, 90, 120].map((m) => (
                      <option key={m} value={m}>{m} min</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <div className="overline mb-1">Note (optional)</div>
                  <textarea
                    rows={2}
                    value={modal.note || ""}
                    onChange={(e) => setModal({ ...modal, note: e.target.value })}
                    className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none text-sm"
                    data-testid="cal-modal-note"
                  />
                </label>
                <button
                  type="button"
                  disabled={saving}
                  onClick={createSession}
                  className="w-full blaze-btn px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-50"
                  data-testid="cal-modal-save-session"
                >
                  {saving ? "Booking…" : "Book session"}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <label className="block">
                  <div className="overline mb-1">End (hour)</div>
                  <select
                    value={modal.block_end_hour}
                    onChange={(e) => setModal({ ...modal, block_end_hour: e.target.value })}
                    className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none text-sm"
                    data-testid="cal-modal-block-end"
                  >
                    {HOURS.filter((h) => h > modal.hour).concat(21).map((h) => (
                      <option key={h} value={h}>{fmtHour(h)}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <div className="overline mb-1">Reason (optional)</div>
                  <input
                    type="text"
                    value={modal.reason}
                    onChange={(e) => setModal({ ...modal, reason: e.target.value })}
                    placeholder="Vacation, admin block, lunch…"
                    className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none text-sm"
                    data-testid="cal-modal-reason"
                  />
                </label>
                <button
                  type="button"
                  disabled={saving}
                  onClick={createBlock}
                  className="w-full blaze-btn px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-50"
                  data-testid="cal-modal-save-block"
                >
                  {saving ? "Saving…" : "Block time"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
